import { memo, useEffect, useRef, useState } from 'react';
import { TranscriptParagraph, TranscriptSelection } from '../types';
import IconButton from '@/components/atoms/Button/IconButton';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';

const MAX_TRANSCRIPT_INLINE_WORD_EDIT_INPUT_LENGTH = 40;

function TranscriptInlineEdit({
  editingTranscriptRef,
  editingContent,
  paragraphs,
  transcriptSelection,
  onCancelEditing,
  transcriptContainer,
  onEditActionTriggered
}: {
  editingTranscriptRef: React.MutableRefObject<HTMLInputElement | null>;
  editingContent: string;
  paragraphs: TranscriptParagraph[];
  transcriptSelection: TranscriptSelection;
  onCancelEditing: () => void;
  transcriptContainer: React.MutableRefObject<HTMLDivElement | null>;
  onEditActionTriggered: () => void;
}) {
  const { clipData } = useClipsContext();
  const [correctInputValue, setCorrectInputValue] = useState(editingContent);
  const inlineEditPopoverRef = useRef<HTMLDivElement>(null);
  const [popupCalculativeStyles, setPopupCalculativeStyles] = useState<{
    top: number;
    left: number;
  }>();

  useEffect(() => {
    requestAnimationFrame(() => {
      if (editingTranscriptRef.current) {
        editingTranscriptRef.current.style.height = `${editingTranscriptRef.current?.scrollHeight}px`;
      }
    });
  }, [editingContent]);

  const correctTranscript = () => {
    onEditActionTriggered();
    if (transcriptSelection.startSegmentId) {
      updateClipData({
        clipId: clipData.id,
        actionType: CLIP_UPDATE_TYPE.EDIT_WORD,
        words: transcriptSelection.words[transcriptSelection.startSegmentId] || [],
        newValue: correctInputValue,
        compositeId: transcriptSelection.startSegmentId
      });
    }
    onCancelEditing();
  };

  const correctAll = () => {
    onEditActionTriggered();
    if (transcriptSelection.startSegmentId) {
      const startSegmentId = transcriptSelection.startSegmentId;
      updateClipData({
        clipId: clipData.id,
        actionType: CLIP_UPDATE_TYPE.CORRECT_ALL,
        paragraphs,
        editingContent,
        newValue: correctInputValue,
        selectedWords: transcriptSelection.words[startSegmentId] || [],
        compositeId: startSegmentId
      });
    }
    onCancelEditing();
  };

  const isNewInputValid = correctInputValue.replaceAll(' ', '').length > 0;

  useEffect(() => {
    if (!transcriptContainer.current || !inlineEditPopoverRef.current || !editingTranscriptRef.current) {
      return;
    }

    const parentRect = transcriptContainer.current.getBoundingClientRect();
    const anchorRect = editingTranscriptRef.current.getBoundingClientRect();
    const popupRect = inlineEditPopoverRef.current.getBoundingClientRect();

    const top = anchorRect.top - popupRect.height - 8;
    let left = anchorRect.left + anchorRect.width / 2 - popupRect.width / 2;

    if (left < parentRect.left + 8) {
      left = anchorRect.left;
    }

    if (left + popupRect.width > parentRect.right) {
      left = anchorRect.right - popupRect.width;
    }

    setPopupCalculativeStyles({ top, left });
  }, []);

  return (
    <div className="relative inline-flex px-2" ref={editingTranscriptRef}>
      <div
        className="fixed z-50 flex h-fit items-center space-x-0.5 rounded-lg border border-slate-300 bg-white pr-5 shadow-lg"
        style={popupCalculativeStyles || { visibility: 'hidden' }}
        ref={inlineEditPopoverRef}
      >
        <IconButton
          variation="text"
          icon="IconCheck"
          size="base"
          content="Correct"
          trackingId="correct-trascript-confirm"
          disabled={!isNewInputValid}
          onClick={correctTranscript}
        />
        <IconButton
          variation="text"
          icon="IconChecks"
          size="base"
          buttonClassName="whitespace-nowrap"
          content="Correct all"
          onClick={correctAll}
          disabled={!isNewInputValid}
          trackingId="correct-all-trascript-confirm"
        />
        <IconButton
          variation="text"
          icon="IconX"
          size="base"
          content=""
          trackingId="correct-transcript-cancel"
          onClick={onCancelEditing}
        />
      </div>
      <input
        autoFocus
        id="editing-block"
        size={Math.min(correctInputValue.length, MAX_TRANSCRIPT_INLINE_WORD_EDIT_INPUT_LENGTH) || 1}
        value={correctInputValue}
        onChange={e => setCorrectInputValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && isNewInputValid) {
            e.preventDefault();
            correctTranscript();
          }
        }}
        className="block w-fit resize-none overflow-hidden rounded border border-theme-input-focus p-1 text-black selection:bg-transcript-highlight-bg focus:border-none focus:outline-none focus:ring-theme-input-focus"
      />
    </div>
  );
}

export default memo(TranscriptInlineEdit);
